import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserSettingLayoutContainer } from "../UserSettingLayoutContainer";
import { useLanguage, useTranslate } from "@obsbot/remo-i18n";
import { Avatar, Input, Skeleton, Upload, InputRef } from "antd";
import { UploadRequestOption } from "rc-upload/lib/interface";
import {
    channelRoomStore,
    channelStore,
    chatStore,
    friendStore,
    useGlobalStore,
} from "@obsbot/remo-stores";
import {
    eventBus,
    filterInputValue,
    limitInputLength,
    RememberPassword,
    useGenerateAvatar,
} from "@obsbot/remo-utils";
import {
    AccountVerifyModal,
    AvatarBackgroundMap,
    BindNewAccountModal,
    ChangePasswordModal,
    ConfirmEditModal,
    DeregisterModal,
    FileType,
    PersonalCard,
    useSafePromise,
    VerifyType,
} from "remo-components";
import {
    bindNewAccount,
    deregisterAccount,
    sendVerifyCode,
    updateUserInfo,
    UpdateUserInfoData,
    uploadFile,
    verifyCodeVal,
} from "@obsbot/remo-server-api";
import { v4 as uuidV4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { routeConfig, RouteNameType } from "../../route-config";
import "./index.less";
import { RightOutlined } from "@ant-design/icons";
import { useClickAway } from "react-use";

const AccountSettingPage = (): React.ReactElement => {
    const globalStore = useGlobalStore();
    const t = useTranslate();
    const lang = useLanguage();
    const [userName, setUserName] = useState(globalStore.userInfo?.nickname || "");
    // 账号修改验证的弹出框
    const [verifyVisible, setVerifyVisible] = useState(false);
    // 密码修改验证的弹出框
    const [verifyPwdVisible, setVerifyPwdVisible] = useState(false);
    // 账号修改验证的验证码是否出错
    const [isCodeError, setIsCodeError] = useState(false);
    // 注销须知的弹出框
    const [deregisterVisible, setDeregisterVisible] = useState(false);
    // AccountVerifyModal弹出框的类型
    const [accountVerifyModalType, setAccountVerifyModalType] = useState<VerifyType>(
        VerifyType.Account,
    );
    // 是否正在修改账号
    const [isEditingAccount, setIsEditingAccount] = useState(false);
    // 密码修改的弹出框
    const [changePasswordVisible, setChangePasswordVisible] = useState(false);
    // 密码和原密码相同的错误提示
    const [isSamePassword, setIsSamePassword] = useState(false);
    // 是否重新绑定账号
    const [isRebindAccount, setIsRebindAccount] = useState(false);
    const sp = useSafePromise();
    const navigate = useNavigate();
    // 是否正在上传的loading
    const [isLoading, setIsLoading] = useState(false);
    // 头像是否加载完成
    const [isAvatarLoaded, setIsAvatarLoaded] = useState(true);
    // 忘记密码校验过的验证码
    const [forgetCode, setForgetCode] = useState("");
    // 是否正在拼音
    const isComposing = useRef(false);
    // 是否提示用户保存当前用户名
    const [isPromptSaveUsername, setIsPromptSaveUsername] = useState(false);
    // 用户输入框
    const userInputContainerRef = useRef<HTMLDivElement>(null);
    // Antd输入组件
    const usernameInputRef = useRef<InputRef>(null);
    const defaultAvatarBackgroundIndex = useMemo(() => {
        const match = globalStore.userInfo?.logo.match(/default_(\d+)/);
        const index = match ? parseInt(match[1], 10) : -1;
        return index;
    }, [globalStore.userInfo?.logo]);
    const defaultAvatar = useGenerateAvatar(
        userName,
        AvatarBackgroundMap[defaultAvatarBackgroundIndex],
    );
    const defaultAvatarURL = useMemo(() => {
        if (defaultAvatarBackgroundIndex === -1) {
            return "";
        }
        return defaultAvatar.toDataURL();
    }, [defaultAvatarBackgroundIndex, defaultAvatar]);
    useClickAway(userInputContainerRef, () => {
        if (isEditingAccount && userName !== globalStore.userInfo?.nickname) {
            setIsPromptSaveUsername(true);
        }
        if (userName === globalStore.userInfo?.nickname) {
            setIsEditingAccount(false);
        }
    });
    useEffect(() => {
        if (!verifyVisible || !verifyPwdVisible) {
            setIsCodeError(false);
        }
    }, [verifyVisible, verifyPwdVisible]);
    // 获取验证码
    const onGetCode = async (
        type: "changePwd" | "verify" | "user_cancel" | "change_account",
        account?: string,
    ): Promise<boolean> => {
        let msg_type = "";
        switch (type) {
            case "changePwd": {
                msg_type = "forget_pwd";
                break;
            }
            case "verify": {
                msg_type = "safe_check";
                break;
            }
            case "user_cancel":
            case "change_account": {
                msg_type = type;
                break;
            }
        }
        console.log(`[WEB] 获取[${msg_type}]验证码`);
        const globalStore = useGlobalStore.getState();
        if (!globalStore.userInfo) {
            console.log("[WEB] ERROR 不符合预期, 全局仓库userInfo为空");
            return false;
        }
        try {
            const result = await sendVerifyCode({
                account: (account || globalStore.userInfo.phone || globalStore.userInfo.email)!,
                msg_type,
                client_type: (window as any).isElectron ? "game_client" : "web",
                mobile_id: uuidV4(),
                lan: lang === "zh-CN" ? "cn" : lang,
            });
            console.log(`[WEB] DEBUG [${type}] 发送验证码返回: `, result);
            return true;
        } catch (error) {
            console.log(`[WEB] DEBUG [${type}] 发送验证码返回错误: `, error);
            return false;
        }
    };
    // 验证验证码是否正确
    const onNext = async (code: string): Promise<void> => {
        const isVerifyAccount = accountVerifyModalType === VerifyType.Account;
        const isForgetPass = accountVerifyModalType === VerifyType.ForgetPass;
        console.log(
            `[WEB] DEBUG [${isVerifyAccount ? "verify" : "Identification"}] 查看验证码是否正确`,
            code,
        );
        if (!globalStore.userInfo) {
            console.log("[WEB] ERROR 不符合预期, 全局仓库userInfo为空");
            return;
        }
        try {
            if (!isVerifyAccount && !isForgetPass) {
                const deregister = await sp(
                    deregisterAccount({
                        verification_code: code,
                        soft_delete_flag: true,
                        mobile_id: uuidV4(),
                    }),
                );
                if (deregister) {
                    setVerifyVisible(false);
                    setIsCodeError(false);
                    handleQuitAccount();
                    return;
                }
            }
            const validRes = await sp(
                verifyCodeVal({
                    account: (globalStore.userInfo.phone || globalStore.userInfo.email)!,
                    msg_type: isForgetPass
                        ? "forget_pwd"
                        : isVerifyAccount
                          ? "safe_check"
                          : "user_cancel",
                    client_type: (window as any).isElectron ? "game_client" : "web",
                    mobile_id: uuidV4(),
                    verification_code: code,
                    force_flag: 0,
                }),
            );
            if (validRes && validRes.valid && isForgetPass) {
                setVerifyPwdVisible(false);
                setIsCodeError(false);
                setForgetCode(code);
                setChangePasswordVisible(true);
            } else if (validRes && validRes.valid && isVerifyAccount) {
                setVerifyVisible(false);
                setIsCodeError(false);
                setIsRebindAccount(true);
            } else if (validRes && validRes.valid && !isVerifyAccount) {
                setVerifyVisible(false);
                setIsCodeError(false);
                handleQuitAccount();
            } else {
                setIsCodeError(true);
            }
        } catch (error) {
            const isErrorMsgExist = error && error.error_msg;
            if (isErrorMsgExist && error.error_msg.includes("user verification code is invalid")) {
                setIsCodeError(true);
                return;
            }
            console.log("[WEB] DEBUG 验证验证码请求出错: ", error);
        }
    };
    // 确定注销的回调
    const handleDeregister = (): void => {
        console.log("[WEB] 用户选择注销");
        setDeregisterVisible(false);
        setAccountVerifyModalType(VerifyType.Identification);
        setVerifyVisible(true);
    };
    // 点击修改/确定的回调
    const handleEditOrConfirm = async (): Promise<void> => {
        if (isLoading) {
            return;
        }
        if (userName === "") {
            eventBus.emit("show-message", {
                message: t("noInputSingle", { text1: t("username") }),
                type: "warning",
            });
            return;
        }
        if (isEditingAccount && userName !== globalStore.userInfo!.nickname) {
            try {
                setIsLoading(true);
                const updatePayload: UpdateUserInfoData = {
                    nickname: userName,
                };
                const getCanvasBlob = (): Promise<Blob | null> => {
                    return new Promise(resolve => {
                        defaultAvatar.toBlob(blob => resolve(blob), "image/png");
                    });
                };
                if (defaultAvatarBackgroundIndex !== -1) {
                    const blob = await getCanvasBlob();
                    if (!blob) {
                        console.error("[WEB] ERROR 获取canvas的blob失败", defaultAvatar);
                        return;
                    }
                    const formData = new FormData();
                    formData.append("files", blob, `default_${defaultAvatarBackgroundIndex}-.png`);
                    const res = await uploadFile({
                        type: "normal",
                        md5_flag: true,
                        files: formData,
                    });
                    setIsAvatarLoaded(false);
                    if (res.files?.[0]?.url) {
                        updatePayload.logo = res.files[0].url;
                    }
                }
                await updateUserInfo(updatePayload);
                await globalStore.getUserInfo(globalStore.userInfo?.notSet || false);
                if (updatePayload.logo) {
                    const avatarUrl = updatePayload.logo;
                    const img = new Image();
                    img.src = avatarUrl;
                    img.onload = () => {
                        setIsAvatarLoaded(true);
                    };
                }
            } catch (error) {
                setUserName(globalStore.userInfo?.nickname || "");
            } finally {
                setIsLoading(false);
            }
        }
        setIsEditingAccount(!isEditingAccount);
    };
    // 修改密码的回调
    const handleChangePwd = async (code: string, newPass: string): Promise<void> => {
        console.log("[WEB] 用户选择修改密码", code);
        if (!globalStore.userInfo) {
            console.log("[WEB] ERROR 不符合预期, 全局仓库userInfo为空");
            return;
        }
        try {
            // await changePassword({
            //     account: (globalStore.userInfo.phone || globalStore.userInfo.email)!,
            //     verification_code: code,
            //     password: newPass,
            //     mobile_id: uuidV4(),
            //     client_type: (window as any).isElectron ? "game_client" : "web",
            // });
            await updateUserInfo({ password: newPass });
            eventBus.emit("show-message", {
                message: t("changePass.title") + t("Success"),
                type: "success",
            });
            RememberPassword.clearPassword();
            setChangePasswordVisible(false);
            setIsCodeError(false);
        } catch (error) {
            const isErrorMsgExist = error && error.error_msg;
            if (isErrorMsgExist && error.error_msg.includes("user verification code is invalid")) {
                setIsCodeError(true);
                return;
            } else if (isErrorMsgExist && error.error_msg.includes("user pwd can not be same")) {
                setIsSamePassword(true);
                return;
            }
            console.log("[WEB] DEBUG 修改密码请求出错: ", error);
        }
    };
    // 重新绑定账号的回调
    const handleRebindAccount = async (code: string, account: string): Promise<void> => {
        try {
            const res = await bindNewAccount({
                new_account: account,
                verification_code: code,
                mobile_id: uuidV4(),
            });
            console.log("[WEB] DEBUG 绑定新账号成功", res);
            eventBus.emit("show-message", {
                message: t("bindNewAccount.title") + t("Success"),
                type: "success",
            });
            RememberPassword.clearPassword();
            setIsRebindAccount(false);
            handleQuitAccount();
        } catch (error) {
            const isErrorMsgExist = error && error.error_msg;
            if (isErrorMsgExist && error.error_msg.includes("user verification code is invalid")) {
                setIsCodeError(true);
                return;
            }
            console.log("[WEB] DEBUG 绑定新的账号请求出错: ", error);
        }
    };
    const renderEmail = useMemo(() => {
        if (!globalStore.userInfo) {
            return "";
        }
        const phone = globalStore.userInfo?.phone;
        if (phone) {
            return phone.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
        }
        const email = globalStore.userInfo.email!;
        return email.replace(/^[^@]+/, match => "*".repeat(match.length));
    }, [globalStore.userInfo]);
    const renderPassword = useMemo(() => {
        return "********";
    }, []);
    const handleQuitAccount = useCallback(async () => {
        globalStore.destroy();
        await channelRoomStore.destroy();
        channelStore.destroy();
        await friendStore.destroy();
        await chatStore.destroy();
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        navigate(routeConfig[RouteNameType.LoginPage].path);
    }, []);

    // 处理派发事件告诉侧边栏头像在重新加载
    useEffect(() => {
        eventBus.emit("load-new-avatar", isAvatarLoaded);
    }, [isAvatarLoaded]);
    const handleUploadAvatar = async ({ file }: UploadRequestOption): Promise<void> => {
        const formData = new FormData();
        formData.append("files", file);
        try {
            setIsLoading(true);
            const uploadRes = await uploadFile({
                type: "normal",
                md5_flag: true,
                files: formData,
            });
            console.log("[WEB] DEBUG 查看上传头像的返回: ", uploadRes);
            // 预加载一下图片
            setIsAvatarLoaded(false);
            const avatarUrl = uploadRes.files[0].url;
            const img = new Image();
            img.src = avatarUrl;
            img.onload = () => {
                setIsAvatarLoaded(true);
            };
            const updateRes = await updateUserInfo({ logo: avatarUrl });
            const userInfo = await globalStore.getUserInfo(globalStore.userInfo?.notSet || false);
            console.log("[WEB] DEBUG 查看更新用户信息以及查询的返回: ", updateRes, userInfo);
        } catch (error) {
            console.log("[WEB] DEBUG 上传头像出错", error);
        } finally {
            setIsLoading(false);
        }
    };
    const beforeUpload = (file: FileType): boolean => {
        if (isLoading) {
            eventBus.emit("show-message", {
                message: t("uploadLimit.uploading"),
                type: "error",
            });
            return false;
        }
        const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
        if (!isJpgOrPng) {
            eventBus.emit("show-message", {
                message: t("uploadLimit.picType"),
                type: "error",
            });
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            eventBus.emit("show-message", {
                message: t("uploadLimit.sizeLimit"),
                type: "error",
            });
            return false;
        }
        return isJpgOrPng && isLt5M;
    };
    return (
        <UserSettingLayoutContainer>
            <div className="account-setting-container">
                <div className="account-setting-page" style={{ position: "relative" }}>
                    <div className="setting-page-title">
                        {t("settings.account-setting.personalProfile")}
                    </div>
                    <div className="setting-page-content account-setting-info-container">
                        <div className="account-setting-info">
                            <div className="setting-show-item-between">
                                <div
                                    className="setting-page-label"
                                    style={{ justifyContent: "left" }}
                                >
                                    {t("settings.account-setting.avatar")}
                                </div>
                                <div className="account-setting-change-avatar-btn">
                                    <Upload
                                        accept=".jpg,.png,.jpeg"
                                        beforeUpload={beforeUpload}
                                        customRequest={handleUploadAvatar}
                                        itemRender={() => null}
                                        maxCount={1}
                                        name="files"
                                    >
                                        {isAvatarLoaded ? (
                                            <Avatar
                                                src={
                                                    defaultAvatarBackgroundIndex === -1
                                                        ? globalStore.userInfo?.logo ||
                                                          globalStore.userInfo?.avatar
                                                        : defaultAvatarURL
                                                }
                                            />
                                        ) : (
                                            <Skeleton.Avatar
                                                active={true}
                                                shape="circle"
                                                size={50}
                                            />
                                        )}
                                    </Upload>
                                    {isAvatarLoaded && (
                                        <div className="hover-cover">
                                            <svg
                                                fill="none"
                                                height="18"
                                                version="1.1"
                                                viewBox="0 0 20 18"
                                                width="20"
                                                xmlns="http://www.w3.org/2000/svg"
                                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                            >
                                                <g>
                                                    <path
                                                        d="M13.466999,0C14.234458,-0.000014252533,14.912377,0.48649886,15.138999,1.199919L15.684998,2.9189188L17.25,2.9189188C18.768749,2.9189188,19.999998,4.1168923,19.999998,5.594594L19.999998,15.324324C19.999998,16.802027,18.768749,18,17.25,18L2.7499995,18C1.2312498,18,0,16.802027,0,15.324324L0,5.594594C0,4.1168914,1.2312498,2.9189188,2.7499995,2.9189188L4.3150001,2.9189188L4.8609991,1.199919C5.0876198,0.48649862,5.7655401,-0.000014252533,6.532999,0L13.466999,0ZM9.999999,6.3243237C7.9289999,6.3243237,6.25,7.9579458,6.25,9.9729729C6.25,11.988,7.9289999,13.621619,9.999999,13.621619C12.070999,13.621619,13.75,11.987999,13.75,9.9729719C13.75,7.9579453,12.070999,6.3243227,9.999999,6.3243237ZM9.999999,12.162161C11.24264,12.162161,12.249999,11.182028,12.249999,9.9729719C12.249999,8.763916,11.24264,7.783782,9.999999,7.783782C8.7573586,7.783782,7.75,8.763916,7.75,9.9729719C7.75,11.182028,8.7573586,12.162161,9.999999,12.162161Z"
                                                        fill="#FFFFFF"
                                                        fillOpacity="1"
                                                        // @ts-ignore
                                                        style={{ mixBlendMode: "passthrough" }}
                                                    />
                                                </g>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="setting-show-item-between">
                                <div
                                    className="setting-page-label"
                                    style={{ justifyContent: "left" }}
                                >
                                    {t("username")}
                                </div>
                                <div
                                    ref={userInputContainerRef}
                                    className="account-setting-username-input account-setting-input"
                                >
                                    <Input
                                        ref={usernameInputRef}
                                        className={
                                            isEditingAccount
                                                ? "account-setting-username account-setting-username-editing"
                                                : "account-setting-username"
                                        }
                                        disabled={isLoading}
                                        readOnly={!isEditingAccount}
                                        suffix={
                                            <div
                                                className={
                                                    isEditingAccount
                                                        ? "setting-edit setting-edit-confirm"
                                                        : "setting-edit"
                                                }
                                                onClick={handleEditOrConfirm}
                                            >
                                                {isEditingAccount
                                                    ? t("settings.account-setting.confirm")
                                                    : t("settings.account-setting.edit")}
                                            </div>
                                        }
                                        value={userName}
                                        onChange={e => {
                                            let filterVal = filterInputValue(e.target.value);
                                            if (!isComposing.current) {
                                                const { val, overLimit } =
                                                    limitInputLength(filterVal);
                                                if (overLimit) {
                                                    eventBus.emit("show-message", {
                                                        message: t("nameLengthOverLimit"),
                                                        type: "warning",
                                                    });
                                                }
                                                filterVal = val;
                                            }
                                            setUserName(filterVal);
                                        }}
                                        onClick={() => {
                                            if (!isEditingAccount) {
                                                setIsEditingAccount(true);
                                            }
                                        }}
                                        onCompositionEnd={e => {
                                            isComposing.current = false;
                                            const finalValue = (e.target as any).value;
                                            const { val, overLimit } = limitInputLength(finalValue);
                                            if (overLimit) {
                                                eventBus.emit("show-message", {
                                                    message: t("nameLengthOverLimit"),
                                                    type: "warning",
                                                });
                                            }
                                            setUserName(val);
                                        }}
                                        onCompositionStart={() => (isComposing.current = true)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") {
                                                handleEditOrConfirm();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="account-setting-self-card"></div>
                    </div>
                    <div className="setting-page-personal-card">
                        <PersonalCard
                            avatarInfo={{
                                avatar: isAvatarLoaded
                                    ? globalStore.userInfo?.logo || globalStore.userInfo?.avatar
                                    : "loading",
                                userID: globalStore.userInfo?.user_id || "",
                                userName: globalStore.userInfo?.nickname || "",
                                status: globalStore.userStatus,
                                size: 40,
                            }}
                            showSetting={false}
                        />
                    </div>
                </div>
                <div className="account-setting-page" style={{ marginTop: 32 }}>
                    <div className="setting-page-title">
                        {t("settings.account-setting.accountSafe")}
                    </div>
                    <div className="setting-page-content account-edit-container">
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                {t("settings.account-setting.account")}
                            </div>
                            <div className="account-setting-email-input account-setting-input">
                                <Input
                                    className="account-setting-email"
                                    readOnly={true}
                                    suffix={
                                        <div
                                            className="setting-edit"
                                            onClick={() => {
                                                setVerifyVisible(true);
                                                setAccountVerifyModalType(VerifyType.Account);
                                            }}
                                        >
                                            {t("settings.account-setting.edit")}
                                        </div>
                                    }
                                    value={renderEmail}
                                    onClick={() => {
                                        setVerifyVisible(true);
                                        setAccountVerifyModalType(VerifyType.Account);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">{t("login-page.password")}</div>
                            <div className="account-setting-password-input account-setting-input">
                                <Input
                                    className="account-setting-password"
                                    readOnly={true}
                                    suffix={
                                        <div
                                            className="setting-edit"
                                            onClick={() => {
                                                setAccountVerifyModalType(VerifyType.ForgetPass);
                                                setVerifyPwdVisible(true);
                                            }}
                                        >
                                            {t("settings.account-setting.edit")}
                                        </div>
                                    }
                                    value={renderPassword}
                                    onClick={() => {
                                        setAccountVerifyModalType(VerifyType.ForgetPass);
                                        setVerifyPwdVisible(true);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="account-setting-page" style={{ marginTop: 32 }}>
                    <div className="setting-page-title">
                        {t("settings.account-setting.deregister")}
                    </div>
                    <div className="setting-page-content" style={{ paddingTop: 0 }}>
                        <div
                            className="account-setting-deregister"
                            onClick={() => setDeregisterVisible(true)}
                        >
                            <span>{t("deregisterAccount.title")}</span>
                            <RightOutlined />
                        </div>
                    </div>
                </div>
            </div>
            <BindNewAccountModal
                isCodeError={isCodeError}
                setIsCodeError={setIsCodeError}
                setVisible={setIsRebindAccount}
                visible={isRebindAccount}
                onConfirm={handleRebindAccount}
                onGetCode={account => onGetCode("change_account", account)}
            />
            <ChangePasswordModal
                isPassSameError={isSamePassword}
                setIsPassSameError={setIsSamePassword}
                setVisible={setChangePasswordVisible}
                validCode={forgetCode}
                visible={changePasswordVisible}
                onConfirm={handleChangePwd}
            />
            <AccountVerifyModal
                account={renderEmail}
                isCodeError={isCodeError}
                setIsCodeError={setIsCodeError}
                setVisible={setVerifyVisible}
                type={accountVerifyModalType}
                visible={verifyVisible}
                onGetCode={() =>
                    onGetCode(
                        accountVerifyModalType === VerifyType.Account
                            ? "verify"
                            : accountVerifyModalType === VerifyType.ForgetPass
                              ? "changePwd"
                              : "user_cancel",
                    )
                }
                onNext={onNext}
            />
            <AccountVerifyModal
                account={renderEmail}
                isCodeError={isCodeError}
                setIsCodeError={setIsCodeError}
                setVisible={setVerifyPwdVisible}
                type={accountVerifyModalType}
                visible={verifyPwdVisible}
                onGetCode={() =>
                    onGetCode(
                        accountVerifyModalType === VerifyType.Account
                            ? "verify"
                            : accountVerifyModalType === VerifyType.ForgetPass
                              ? "changePwd"
                              : "user_cancel",
                    )
                }
                onNext={onNext}
            />
            <DeregisterModal
                setVisible={setDeregisterVisible}
                visible={deregisterVisible}
                onConfirm={handleDeregister}
            />
            <ConfirmEditModal
                content={t("settings.account-setting.confirmSave")}
                setVisible={setIsPromptSaveUsername}
                visible={isPromptSaveUsername}
                onCancel={() => {
                    setIsEditingAccount(false);
                    setUserName(globalStore.userInfo?.nickname || "");
                    usernameInputRef.current?.blur();
                    setIsPromptSaveUsername(false);
                }}
                onConfirm={() => {
                    handleEditOrConfirm();
                    setIsPromptSaveUsername(false);
                }}
            />
        </UserSettingLayoutContainer>
    );
};

export default AccountSettingPage;
